import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled, { keyframes } from 'styled-components';
import { designerItemsAPI } from '../../api/designerItemsAPI';
import { artistItemsAPI } from '../../api/artistItemsAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import { getArtistItemPublicPath, getDesignerItemPublicPath, getListingLinkState } from '../../utils/itemLinks';
import ItemFavoriteButton from '../../components/items/ItemFavoriteButton';
import { resolveListingBrand } from '../../components/items/ListingDetailShared';
import GuestAuthPrompt from '../../components/auth/GuestAuthPrompt';
import { FAVORITE_KIND } from '../../utils/favoriteKeys';
import { canOwnerAddCatalogItems } from '../../utils/shopOwnerPortal';
import { customizationsAPI } from '../../api/customizationsAPI';

/* ─── Animations ──────────────────────────────────────────────── */
const fadeUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; }`;

/* ─── Layout ──────────────────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  animation: ${fadeUp} 0.4s ease;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 3rem 1rem 2rem;
  text-align: center;
  color: white;
  position: relative;
`;

const LogoRing = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  border: 4px solid rgba(255,255,255,0.8);
  overflow: hidden;
  margin: 0 auto 1rem;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const ShopName = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
`;

const Bio = styled.p`
  font-size: 1rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto 1.25rem;
  line-height: 1.6;
`;

const AuraBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255,255,255,0.18);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 2rem;
  padding: 0.45rem 1.1rem;
  font-size: 0.95rem;
  font-weight: 600;
`;

const StatusBanner = styled.div`
  background: #fef3c7;
  border-bottom: 2px solid #f59e0b;
  padding: 0.75rem 1rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: #92400e;
`;

/* ─── Content area ─────────────────────────────────────────────── */
const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1rem;
`;

/* ─── Tabs ─────────────────────────────────────────────────────── */
const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 2rem;
  background: white;
  sticky: top;
`;

const TabBtn = styled.button`
  padding: 1rem 1.5rem;
  border: none;
  background: transparent;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${p => p.$active ? '#764ba2' : '#6b7280'};
  border-bottom: 3px solid ${p => p.$active ? '#764ba2' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: -2px;
  &:hover { color: #764ba2; }
`;

/* ─── Catalog ──────────────────────────────────────────────────── */
const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.25rem;
`;

const ItemCard = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  background: transparent;
  overflow: hidden;
  border: none;
  transition: opacity 0.15s;
  &:hover { opacity: 0.92; }
`;

const ItemImg = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  overflow: hidden;
  position: relative;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
  }
`;

const ItemBody = styled.div`padding: 0.55rem 0.15rem 0.25rem;`;

const ItemPrice = styled.p`
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.2rem;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: #64748b;
  min-width: 0;
`;

const MetaBrand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

/* ─── Reviews ──────────────────────────────────────────────────── */
const ReviewSection = styled.div``;

const AuraStats = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  margin-bottom: 1.5rem;
`;

const BigScore = styled.div`
  font-size: 3.5rem;
  font-weight: 900;
  color: #764ba2;
  line-height: 1;
`;

const Stars = styled.div`
  font-size: 1.4rem;
  margin: 0.25rem 0;
`;

const AuraLevel = styled.p`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
`;

const ReviewCount = styled.p`
  font-size: 0.85rem;
  color: #9ca3af;
  margin: 0;
`;

const ReviewCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.25rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  margin-bottom: 0.75rem;
  border: 1px solid #f0f0f0;
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const ReviewerName = styled.span`
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
`;

const ReviewDate = styled.span`
  font-size: 0.8rem;
  color: #9ca3af;
`;

const ReviewComment = styled.p`
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
`;

const NoContent = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #9ca3af;
  font-size: 1rem;
`;

/* ─── Write a review ───────────────────────────────────────────── */
const ReviewForm = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  margin-bottom: 1.5rem;
  border: 1px solid #e5e7eb;
`;

const ReviewFormTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem;
`;

const StarPicker = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const StarBtn = styled.button`
  font-size: 1.75rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: transform 0.15s;
  &:hover { transform: scale(1.2); }
`;

const CommentInput = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1.5px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #764ba2; }
`;

const SubmitReviewBtn = styled.button`
  margin-top: 0.75rem;
  padding: 0.65rem 1.5rem;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover:not(:disabled) { opacity: 0.88; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* ─── Policies ─────────────────────────────────────────────────── */
const PolicyBox = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  margin-bottom: 1.25rem;
  border-left: 4px solid #764ba2;
`;

const PolicyTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PolicyText = styled.p`
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.7;
  margin: 0;
  white-space: pre-wrap;
`;

/* ─── My Shop edit bar ─────────────────────────────────────────── */
const EditBar = styled.div`
  background: #1e293b;
  color: white;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.9rem;
`;

const EditBtn = styled(Link)`
  padding: 0.45rem 1.1rem;
  background: white;
  color: #1e293b;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.85rem;
  text-decoration: none;
  &:hover { background: #f1f5f9; }
`;

const ManagePanel = styled.div`
  padding: 1rem;
  background: #f6f1ea;
`;

const ManageCard = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  background: #fffdf8;
  border: 1px solid #eadfce;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(63, 46, 31, 0.08);
  padding: 1.15rem 1.25rem 0.65rem;
`;

const ManageEyebrow = styled.div`
  color: #b45309;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
`;

const ManageTitle = styled.h2`
  color: #3f2e1f;
  font-size: 1.35rem;
  font-weight: 800;
  margin: 0.2rem 0 0.4rem;
`;

const ManageBody = styled.p`
  color: #7c6a58;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 1rem;
`;

const ManagePrimary = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  padding: 0.7rem 1.15rem;
  background: #b45309;
  color: #fff;
  border-radius: 0.75rem;
  font-weight: 800;
  text-decoration: none;
  margin-bottom: 0.35rem;
  &:hover { color: #fff; opacity: 0.94; }
`;

const ManageRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.95rem 0;
  border-top: 1px solid #eadfce;
  text-decoration: none;
  color: inherit;
  &:hover { opacity: 0.88; }
`;

const ManageRowCopy = styled.div`
  flex: 1;
  min-width: 0;
`;

const ManageRowTitle = styled.div`
  color: #3f2e1f;
  font-size: 1rem;
  font-weight: 800;
`;

const ManageRowHint = styled.div`
  color: #7c6a58;
  font-size: 0.88rem;
  line-height: 1.4;
  margin-top: 0.15rem;
`;

const ManageBadge = styled.span`
  min-width: 1.4rem;
  height: 1.4rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #b45309;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ManageChevron = styled.span`
  color: #b45309;
  font-size: 1.6rem;
  line-height: 1;
  font-weight: 300;
`;

/* ─── Helpers ─────────────────────────────────────────────────── */
const IMAGE_BASE = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1')
  ? 'http://127.0.0.1:8080'
  : '';

const toAbsolute = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${IMAGE_BASE}${url}`;
};

const renderStars = (score) => {
  if (score === null || score === undefined) return '☆☆☆☆☆';
  const full = Math.floor(score);
  const half = score - full >= 0.5;
  return '★'.repeat(full) + (half ? '½' : '') + '☆'.repeat(5 - full - (half ? 1 : 0));
};

/* ─── Component ────────────────────────────────────────────────── */
const ShopDetailPage = () => {
  const { shopRef } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector(s => s.auth);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('catalog');
  const isArtistShop = location.pathname.startsWith('/artist-shop/');
  const shopAPI = isArtistShop ? artistItemsAPI : designerItemsAPI;
  const browsePath = isArtistShop ? '/artists' : '/designers';
  const itemPath = isArtistShop ? getArtistItemPublicPath : getDesignerItemPublicPath;
  const favoriteKind = isArtistShop ? FAVORITE_KIND.ARTIST : FAVORITE_KIND.DESIGNER;

  // Review form state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        let data = null;
        if (isAuthenticated) {
          try {
            const mine = await shopAPI.getMyShop();
            if (mine && shopAPI.buildShopRef(mine) === shopRef) {
              data = mine;
            }
          } catch {
            /* fall through to the public shop page */
          }
        }
        if (!data) {
          data = await shopAPI.getShopByRef(shopRef);
        }
        setShop(data);
      } catch (err) {
        if (err.response?.status === 404) {
          toast.error('Shop not found.');
          navigate(browsePath);
        } else {
          toast.error('Failed to load shop.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [shopRef, navigate, shopAPI, browsePath, isAuthenticated]);

  useEffect(() => {
    const isOwner = Boolean(
      isAuthenticated && user && shop && String(user.id) === String(shop.owner_id),
    );
    if (!isOwner) {
      setPendingRequestCount(0);
      return undefined;
    }
    let cancelled = false;
    const inboxPromise = isArtistShop
      ? customizationsAPI.getArtistInbox()
      : customizationsAPI.getDesignerInbox();
    inboxPromise
      .then((response) => {
        if (cancelled) return;
        const results = Array.isArray(response.results) ? response.results : [];
        setPendingRequestCount(results.filter((request) => request.state === 'sent').length);
      })
      .catch(() => {
        if (!cancelled) setPendingRequestCount(0);
      });
    return () => { cancelled = true; };
  }, [isArtistShop, isAuthenticated, user, shop]);

  const handleSubmitReview = async () => {
    if (!reviewRating) { toast.error('Please select a star rating.'); return; }
    setSubmittingReview(true);
    try {
      const review = await shopAPI.addShopReview(shopRef, reviewRating, reviewComment);
      toast.success('Review submitted!');
      setShop(prev => ({
        ...prev,
        recent_reviews: [review, ...(prev.recent_reviews || [])],
        review_count: (prev.review_count || 0) + 1,
      }));
      setReviewRating(0);
      setReviewComment('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit review.');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading shop…" />;
  if (!shop) return null;

  const isOwner = Boolean(user && String(user.id) === String(shop.owner_id));
  const canAddItems = canOwnerAddCatalogItems(shop, isOwner);
  const addItemPath = isArtistShop ? '/offer-artist-item' : '/offer-designer-item';
  const addItemLabel = isArtistShop ? '+ Add to gallery' : '+ Add item';
  const logoUrl = toAbsolute(shop.logo_url);
  const catalog = shop.catalog || [];
  const reviews = shop.recent_reviews || [];
  const hasPolicies = shop.return_policy || shop.shipping_policy;
  const auraScoreValue = Number.parseFloat(String(shop.aura_score));
  const hasAuraScore = Number.isFinite(auraScoreValue);

  return (
    <Page>
      {isOwner && isArtistShop && (
        <ManagePanel>
          <ManageCard>
            <ManageEyebrow>YOUR GALLERY</ManageEyebrow>
            <ManageTitle>Manage gallery</ManageTitle>
            <ManageBody>
              Add artwork and handle custom painting requests from here.
            </ManageBody>
            {canAddItems && (
              <ManagePrimary to={addItemPath}>{addItemLabel}</ManagePrimary>
            )}
            <ManageRow to="/artist/customization-requests">
              <ManageRowCopy>
                <ManageRowTitle>Customization requests</ManageRowTitle>
                <ManageRowHint>Review painting briefs and send quotes.</ManageRowHint>
              </ManageRowCopy>
              {pendingRequestCount > 0 && <ManageBadge>{pendingRequestCount}</ManageBadge>}
              <ManageChevron aria-hidden="true">›</ManageChevron>
            </ManageRow>
            <ManageRow to="/artist/customization-settings">
              <ManageRowCopy>
                <ManageRowTitle>Customization settings</ManageRowTitle>
                <ManageRowHint>Starting price, canvas size, and whether you accept requests.</ManageRowHint>
              </ManageRowCopy>
              <ManageChevron aria-hidden="true">›</ManageChevron>
            </ManageRow>
          </ManageCard>
        </ManagePanel>
      )}
      {isOwner && !isArtistShop && (
        <EditBar>
          <span>🏪 This is your shop</span>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {canAddItems && (
              <EditBtn to={addItemPath}>
                {addItemLabel}
              </EditBtn>
            )}
            <EditBtn to="/designer-shop/edit">Edit Shop</EditBtn>
            <EditBtn to="/designer/customization-settings">Custom orders</EditBtn>
            <EditBtn to="/designer/customization-requests">
              Order inbox{pendingRequestCount > 0 ? ` (${pendingRequestCount})` : ''}
            </EditBtn>
          </div>
        </EditBar>
      )}
      {!isOwner && !isArtistShop && shop?.id && shop.accepts_customizations !== false ? (
        <EditBar style={{ background: '#eef2ff', borderColor: '#5b21b6', color: '#5b21b6' }}>
          <span>Want something made by this designer?</span>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <EditBtn to={`/custom-order/designers/${shop.id}`}>Request a custom order</EditBtn>
          </div>
        </EditBar>
      ) : null}

      {/* Hero */}
      <Hero>
        <LogoRing>
          {logoUrl
            ? <img src={logoUrl} alt={shop.brand_name} />
            : <span style={{ fontSize: '2.5rem' }}>🏪</span>}
        </LogoRing>
        <ShopName>{shop.brand_name}</ShopName>
        {shop.bio && <Bio>{shop.bio}</Bio>}
        {hasAuraScore && (
          <AuraBadge>
            ⭐ {auraScoreValue.toFixed(1)} · {shop.aura_level}
            <span style={{ opacity: 0.7, fontWeight: 400 }}>({shop.review_count} {shop.review_count === 1 ? 'review' : 'reviews'})</span>
          </AuraBadge>
        )}
        {!hasAuraScore && (
          <AuraBadge>✨ New shop · Be the first to review!</AuraBadge>
        )}

        {/* Social links */}
        {(shop.instagram_handle || shop.social_link) && (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '0.85rem' }}>
            {shop.instagram_handle && (
              <a
                href={`https://instagram.com/${shop.instagram_handle.replace(/^@/, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '2rem', padding: '0.35rem 0.9rem',
                  color: 'white', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none',
                }}
              >
                📸 {shop.instagram_handle.startsWith('@') ? shop.instagram_handle : `@${shop.instagram_handle}`}
              </a>
            )}
            {shop.social_link && (
              <a
                href={shop.social_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '2rem', padding: '0.35rem 0.9rem',
                  color: 'white', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none',
                }}
              >
                🔗 Visit Page
              </a>
            )}
          </div>
        )}
      </Hero>

      {shop.approval_status === 'PENDING' && (
        <StatusBanner>⏳ This shop is pending admin approval and is only visible to you right now.</StatusBanner>
      )}
      {shop.approval_status === 'REJECTED' && (
        <StatusBanner>❌ This shop was not approved. {shop.rejection_note && `Reason: ${shop.rejection_note}`}</StatusBanner>
      )}

      <Container>
        {/* Tabs */}
        <TabBar>
          <TabBtn $active={activeTab === 'catalog'} onClick={() => setActiveTab('catalog')}>
            🛍️ Catalog ({catalog.length})
          </TabBtn>
          <TabBtn $active={activeTab === 'reviews'} onClick={() => setActiveTab('reviews')}>
            ⭐ Reviews ({shop.review_count || 0})
          </TabBtn>
          {hasPolicies && (
            <TabBtn $active={activeTab === 'policies'} onClick={() => setActiveTab('policies')}>
              📋 Policies
            </TabBtn>
          )}
        </TabBar>

        {/* Catalog tab */}
        {activeTab === 'catalog' && (
          catalog.length > 0 ? (
            <ItemsGrid>
              {catalog.map(item => {
                const img = toAbsolute(item.primary_image || item.images?.[0]?.image);
                return (
                  <ItemCard
                    key={item.id}
                    to={itemPath(item)}
                    state={getListingLinkState(item, isArtistShop ? 'artist' : 'designer')}
                  >
                    <ItemImg>
                      <ItemFavoriteButton
                        itemId={item.id}
                        favoriteKind={favoriteKind}
                        category={item.category}
                      />
                      {img ? <img src={img} alt={item.title} /> : '📦'}
                    </ItemImg>
                    <ItemBody>
                      <ItemPrice>{formatItemPriceEgp(item.price)}</ItemPrice>
                      <ItemMeta>
                        <MetaBrand>{resolveListingBrand(item) || '—'}</MetaBrand>
                      </ItemMeta>
                    </ItemBody>
                  </ItemCard>
                );
              })}
            </ItemsGrid>
          ) : (
            <NoContent>
              <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>📦</div>
              <p>No items in this shop yet.</p>
              {canAddItems && (
                <Link to={addItemPath} style={{ color: '#10b981', fontWeight: 600 }}>
                  {isArtistShop ? 'Add your first piece →' : 'Add your first item →'}
                </Link>
              )}
            </NoContent>
          )
        )}

        {/* Reviews tab */}
        {activeTab === 'reviews' && (
          <ReviewSection>
            {/* Aggregate score */}
            {shop.review_count > 0 && (
              <AuraStats>
                <BigScore>{hasAuraScore ? auraScoreValue.toFixed(1) : '0.0'}</BigScore>
                <div>
                  <Stars>{renderStars(hasAuraScore ? auraScoreValue : 0)}</Stars>
                  <AuraLevel>{shop.aura_level}</AuraLevel>
                  <ReviewCount>{shop.review_count} {shop.review_count === 1 ? 'review' : 'reviews'}</ReviewCount>
                </div>
              </AuraStats>
            )}

            {/* Write a review */}
            {isAuthenticated && !isOwner && (
              <ReviewForm>
                <ReviewFormTitle>Leave a Review</ReviewFormTitle>
                <StarPicker>
                  {[1, 2, 3, 4, 5].map(n => (
                    <StarBtn
                      key={n}
                      onMouseEnter={() => setHoverRating(n)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setReviewRating(n)}
                    >
                      {n <= (hoverRating || reviewRating) ? '⭐' : '☆'}
                    </StarBtn>
                  ))}
                </StarPicker>
                <CommentInput
                  placeholder="Tell others about your experience with this shop… (optional)"
                  value={reviewComment}
                  onChange={e => setReviewComment(e.target.value)}
                  maxLength={500}
                />
                <SubmitReviewBtn onClick={handleSubmitReview} disabled={submittingReview || !reviewRating}>
                  {submittingReview ? 'Submitting…' : 'Submit Review'}
                </SubmitReviewBtn>
              </ReviewForm>
            )}

            {!isAuthenticated && (
              <ReviewForm style={{ textAlign: 'center' }}>
                <GuestAuthPrompt message="Create an account to leave a review" />
              </ReviewForm>
            )}

            {/* Review list */}
            {reviews.length > 0 ? reviews.map(r => (
              <ReviewCard key={r.id}>
                <ReviewHeader>
                  <div>
                    <ReviewerName>{r.reviewer_display_name || r.reviewer_username || 'Customer'}</ReviewerName>
                    <div style={{ fontSize: '1rem', margin: '0.2rem 0' }}>{'⭐'.repeat(r.rating)}</div>
                  </div>
                  <ReviewDate>{new Date(r.created_at).toLocaleDateString()}</ReviewDate>
                </ReviewHeader>
                {r.comment && <ReviewComment>{r.comment}</ReviewComment>}
              </ReviewCard>
            )) : (
              <NoContent>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>💬</div>
                <p>No reviews yet. Be the first to share your experience!</p>
              </NoContent>
            )}
          </ReviewSection>
        )}

        {/* Policies tab */}
        {activeTab === 'policies' && (
          <div>
            {shop.return_policy ? (
              <PolicyBox>
                <PolicyTitle>🔄 Return Policy</PolicyTitle>
                <PolicyText>{shop.return_policy}</PolicyText>
              </PolicyBox>
            ) : (
              <PolicyBox>
                <PolicyTitle>🔄 Return Policy</PolicyTitle>
                <PolicyText style={{ color: '#9ca3af' }}>No return policy specified.</PolicyText>
              </PolicyBox>
            )}
            {shop.shipping_policy ? (
              <PolicyBox>
                <PolicyTitle>🚚 Shipping Policy</PolicyTitle>
                <PolicyText>{shop.shipping_policy}</PolicyText>
              </PolicyBox>
            ) : (
              <PolicyBox>
                <PolicyTitle>🚚 Shipping Policy</PolicyTitle>
                <PolicyText style={{ color: '#9ca3af' }}>No shipping policy specified.</PolicyText>
              </PolicyBox>
            )}
          </div>
        )}
      </Container>

      <div style={{ height: '3rem' }} />
    </Page>
  );
};

export default ShopDetailPage;
